import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS } from '../i18n';
import { API_DOCS_URL, HELP_CENTER_URL, LITE_SIGNUP_URL } from '../data/links';
import { STEP_VISUALS } from '../pages/how-it-works/stepVisuals';

const builtForItemKeys = [
  { key: 'restaurants', icon: 'ri-restaurant-line', href: '/industries/restaurants', color: '#FF6B35', colorLight: '#FFF1EB' },
  { key: 'pharmacies', icon: 'ri-capsule-line', href: '/industries/pharmacies', color: '#00A67E', colorLight: '#E6F9F3' },
  { key: 'retail', icon: 'ri-store-2-line', href: '/industries/retail', color: '#8B5CF6', colorLight: '#F3EEFF' },
  { key: 'ecommerce', icon: 'ri-shopping-cart-line', href: '/industries/ecommerce', color: '#1769ff', colorLight: '#eef5ff' },
  { key: 'couriers', icon: 'ri-truck-line', href: '/industries/couriers', color: '#0EA5E9', colorLight: '#E8F6FE' },
  { key: 'laundry', icon: 'ri-t-shirt-line', href: '/industries/laundry', color: '#EC4899', colorLight: '#FDF2F8' },
  { key: 'flowers', icon: 'ri-flower-line', href: '/industries/flowers', color: '#F59E0B', colorLight: '#FFFBEB' },
  { key: 'water', icon: 'ri-drop-line', href: '/industries/water', color: '#06B6D4', colorLight: '#ECFEFF' },
  { key: 'fuel', icon: 'ri-gas-station-line', href: '/industries/fuel', color: '#EF4444', colorLight: '#FEF2F2' },
  { key: 'manufacturing', icon: 'ri-building-2-line', href: '/industries/manufacturing', color: '#6366F1', colorLight: '#EEF2FF' },
  { key: 'grocery', icon: 'ri-shopping-basket-2-line', href: '/industries/grocery', color: '#059669', colorLight: '#D1FAE5' },
  { key: 'b2b-distribution', icon: 'ri-stack-line', href: '/industries/b2b-distribution', color: '#2563EB', colorLight: '#DBEAFE' },
  { key: 'medical-supplies', icon: 'ri-test-tube-line', href: '/industries/medical-supplies', color: '#0D9488', colorLight: '#CCFBF1' },
  { key: 'field-service', icon: 'ri-tools-line', href: '/industries/field-service', color: '#EA580C', colorLight: '#FFEDD5' },
  { key: 'building-supplies', icon: 'ri-hammer-line', href: '/industries/building-supplies', color: '#B45309', colorLight: '#FEF3C7' },
  { key: 'automotive-parts', icon: 'ri-steering-2-line', href: '/industries/automotive-parts', color: '#DC2626', colorLight: '#FEE2E2' },
  { key: 'contract-logistics', icon: 'ri-ship-line', href: '/industries/contract-logistics', color: '#4338CA', colorLight: '#E0E7FF' },
];

/** Mega menu: same groupings as /industries for faster scanning. */
const BUILT_FOR_GROUPS: { titleKey: string; keys: readonly string[] }[] = [
  { titleKey: 'nav.panels.builtFor.groupFoodHealth', keys: ['restaurants', 'pharmacies', 'grocery', 'medical-supplies'] },
  { titleKey: 'nav.panels.builtFor.groupRetailCommerce', keys: ['retail', 'ecommerce', 'automotive-parts', 'b2b-distribution'] },
  {
    titleKey: 'nav.panels.builtFor.groupLogisticsField',
    keys: [
      'couriers',
      'contract-logistics',
      'manufacturing',
      'field-service',
      'building-supplies',
      'laundry',
      'flowers',
      'water',
      'fuel',
    ],
  },
];

function builtForItemByKey(key: string) {
  return builtForItemKeys.find((i) => i.key === key);
}

const featuresItemKeys = [
  { key: 'brandedTracking', icon: 'ri-map-pin-user-line', href: '/features/branded-tracking', color: '#1769ff', colorLight: '#eef5ff' },
  { key: 'routePlanner', icon: 'ri-route-line', href: '/features/route-planner', tag: 'AI', color: '#00A67E', colorLight: '#E6F9F3' },
  { key: 'aiAgents', icon: 'ri-robot-line', href: '/features/ai-agents', tag: 'New', color: '#8B5CF6', colorLight: '#F3EEFF' },
  { key: 'proofOfDelivery', icon: 'ri-camera-line', href: '/features/proof-of-delivery', color: '#EC4899', colorLight: '#FDF2F8' },
  { key: 'autoAllocation', icon: 'ri-flashlight-line', href: '/features/auto-allocation', tag: 'AI', color: '#F97316', colorLight: '#FFF4EC' },
  { key: 'cashManagement', icon: 'ri-money-dollar-circle-line', href: '/features/cash-management', color: '#F59E0B', colorLight: '#FFFBEB' },
  { key: 'reportsAnalytics', icon: 'ri-bar-chart-box-line', href: '/features/reports-analytics', tag: 'AI', color: '#0EA5E9', colorLight: '#E8F6FE' },
  { key: 'driverApp', icon: 'ri-smartphone-line', href: '/features/driver-app', color: '#1769ff', colorLight: '#eef5ff' },
];

const resourcesItemKeys = [
  { key: 'caseStudies', icon: 'ri-book-open-line', href: '/resources/case-studies' },
  { key: 'blog', icon: 'ri-article-line', href: '/blog' },
  { key: 'roiCalculator', icon: 'ri-calculator-line', href: '/roi-calculator' },
];

const learningResourceKeys = [
  { key: 'blogArticles', icon: 'ri-article-line', href: '/blog' },
  { key: 'roiCalculator', icon: 'ri-calculator-line', href: '/roi-calculator' },
  { key: 'whitepapers', icon: 'ri-file-text-line', href: '/resources/whitepapers', comingSoon: true },
  { key: 'webinars', icon: 'ri-video-line', href: '/blog', comingSoon: true },
];
const successStoryKeys = [
  { key: 'customerSuccess', icon: 'ri-team-line', href: '/resources/case-studies' },
];
const supportKeys = [
  { key: 'helpCenter', icon: 'ri-customer-service-2-line', href: HELP_CENTER_URL },
  { key: 'apiDocs', icon: 'ri-code-s-slash-line', href: API_DOCS_URL },
];

const quickAccessKeys = [
  { key: 'helpCenter', icon: 'ri-customer-service-2-line', href: HELP_CENTER_URL },
  { key: 'contactSupport', icon: 'ri-code-s-slash-line', href: 'https://meetings.hubspot.com/aditya145' },
];

/* ─── Full-width Built For mega panel (Industry Solutions style) ─── */
function BuiltForPanel({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <div className="absolute top-full left-0 right-0 pt-2 z-50" style={{ position: 'fixed', left: 0, right: 0, top: '60px' }}>
      <div className="max-w-7xl mx-auto px-4 pb-2 max-h-[calc(100dvh-4.25rem)] min-h-0 flex flex-col">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-100/80 overflow-hidden flex flex-col min-h-0 max-h-full flex-1">
          {/* Header */}
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50/80 to-white">
            <div>
              <h3 className="text-base font-bold text-gray-900">{t('nav.panels.builtFor.title')}</h3>
              <p className="text-xs text-gray-500 mt-0.5 max-w-md">{t('nav.panels.builtFor.subtitle')}</p>
            </div>
            <Link
              to="/industries"
              onClick={onClose}
              className="flex shrink-0 items-center justify-center space-x-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold text-white hover:opacity-90 transition-opacity shadow-sm"
              style={{ backgroundColor: '#1769ff' }}
            >
              <span>{t('nav.panels.builtFor.viewAll')}</span>
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>

          {/* Three columns — scrolls as one unit so footer stays in view */}
          <div className="grid grid-cols-12 gap-0 flex-1 min-h-0 overflow-y-auto overscroll-contain [scrollbar-width:thin] [scrollbar-color:rgba(0,0,0,0.2)_transparent]">
            {/* Column 1: Industries by group */}
            <div className="col-span-12 lg:col-span-5 p-6 lg:border-r border-gray-100 border-b lg:border-b-0">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#1769ff] flex items-center justify-center shadow-sm">
                  <i className="ri-layout-grid-line text-white text-sm"></i>
                </div>
                <h4 className="text-sm font-bold text-gray-900">{t('nav.panels.builtFor.useCases')}</h4>
              </div>
              <p className="text-xs text-gray-500 mb-4">{t('nav.panels.builtFor.useCasesSub')}</p>
              <div className="space-y-5 pr-2 -mr-1">
                {BUILT_FOR_GROUPS.map((group) => (
                  <div key={group.titleKey}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2.5 pl-0.5">
                      {t(group.titleKey)}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-1">
                      {group.keys.map((key) => {
                        const item = builtForItemByKey(key);
                        if (!item) return null;
                        return (
                          <Link
                            key={item.key}
                            to={item.href}
                            onClick={onClose}
                            className="flex items-start gap-3 p-2.5 rounded-xl border border-transparent hover:bg-gray-50 hover:border-gray-100/90 transition-all group min-w-0 outline-none focus-visible:ring-2 focus-visible:ring-[#1769ff]/40 focus-visible:ring-offset-2"
                          >
                            <div
                              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-white shadow-sm"
                              style={{ backgroundColor: item.color }}
                            >
                              <i className={`${item.icon} text-base`}></i>
                            </div>
                            <div className="min-w-0 pt-0.5">
                              <span className="text-[13px] font-semibold text-gray-900 group-hover:text-[#1769ff] block leading-snug">
                                {t(`nav.items.builtFor.${item.key}.label`)}
                              </span>
                              <span className="text-[11px] text-gray-500 block leading-snug mt-0.5 line-clamp-2">
                                {t(`nav.items.builtFor.${item.key}.description`)}
                              </span>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Column 2: Industry Benefits + Quick Access */}
            <div className="col-span-12 lg:col-span-4 p-6 lg:border-r border-gray-100 border-b lg:border-b-0 bg-gray-50/40">
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#1769ff] flex items-center justify-center">
                  <i className="ri-line-chart-line text-white text-sm"></i>
                </div>
                <h4 className="text-sm font-bold text-gray-900">{t('nav.panels.builtFor.industryBenefitsTitle')}</h4>
              </div>
              <p className="text-[11px] text-gray-500 mb-4">{t('nav.panels.builtFor.industryBenefitsSub')}</p>
              <div className="space-y-2 mb-6">
                {['fasterDeliveries', 'costReduction'].map((k, i) => (
                  <div key={i} className="flex items-center space-x-3 p-2.5 rounded-xl hover:bg-gray-50">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#F3EEFF]">
                      <i className={i === 0 ? 'ri-line-chart-line' : 'ri-money-dollar-circle-line'} style={{ color: '#8B5CF6' }}></i>
                    </div>
                    <div>
                      <span className="text-[13px] font-semibold text-gray-900">{t(`nav.items.industryBenefits.${k}.value`)} {t(`nav.items.industryBenefits.${k}.label`)}</span>
                      <p className="text-[11px] text-gray-500">{t(`nav.items.industryBenefits.${k}.description`)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">{t('nav.panels.builtFor.quickAccessTitle')}</p>
              <div className="space-y-1">
                {quickAccessKeys.map((q, i) => (
                  q.href.startsWith('/') ? (
                    <Link key={i} to={q.href} onClick={onClose} className="flex items-center space-x-2.5 p-2.5 rounded-xl hover:bg-gray-50 group">
                      <div className="w-8 h-8 rounded-lg bg-[#FFF7ED] flex items-center justify-center flex-shrink-0">
                        <i className={`${q.icon} text-[#EA580C] text-sm`}></i>
                      </div>
                      <span className="text-[13px] font-semibold text-gray-800 group-hover:text-gray-900">{t(`nav.items.quickAccess.${q.key}`)}</span>
                    </Link>
                  ) : (
                    <a key={i} href={q.href} target="_blank" rel="noopener noreferrer" onClick={onClose} className="flex items-center space-x-2.5 p-2.5 rounded-xl hover:bg-gray-50 group">
                      <div className="w-8 h-8 rounded-lg bg-[#FFF7ED] flex items-center justify-center flex-shrink-0">
                        <i className={`${q.icon} text-[#EA580C] text-sm`}></i>
                      </div>
                      <span className="text-[13px] font-semibold text-gray-800 group-hover:text-gray-900">{t(`nav.items.quickAccess.${q.key}`)}</span>
                    </a>
                  )
                ))}
              </div>
            </div>

            {/* Column 3: Success Story — align to top of row; avoid h-full + justify-center dead space */}
            <div className="col-span-12 lg:col-span-3 p-6 lg:self-start">
              <div className="rounded-xl border border-gray-100 bg-white overflow-hidden shadow-sm w-full flex flex-col">
                <span className="inline-block mt-4 mx-4 px-2.5 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-wider shrink-0" style={{ backgroundColor: '#00A67E' }}>
                  {t('nav.panels.builtFor.successStory')}
                </span>
                <div className="p-4 flex flex-col">
                  <div className="aspect-video rounded-lg overflow-hidden bg-gray-100 mb-3 shrink-0">
                    <img src="/images/case-studies/case-study-restaurants.png" alt="" className="w-full h-full object-cover" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 leading-snug mb-1">{t('nav.panels.builtFor.successStoryTitle')}</h4>
                  <p className="text-[11px] text-gray-500 leading-relaxed mb-3">{t('nav.panels.builtFor.successStoryDesc')}</p>
                  <Link to="/resources/case-studies" onClick={onClose} className="text-xs font-semibold text-[#1769ff] hover:underline flex items-center space-x-1">
                    <span>{t('nav.panels.builtFor.readCaseStudy')}</span>
                    <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Footer CTA */}
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-6 py-4 bg-gray-50/80 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-700">{t('nav.panels.builtFor.readyToOptimize')}</p>
            <a
              href="https://meetings.hubspot.com/aditya145"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="flex items-center justify-center sm:justify-start space-x-1.5 px-4 py-2.5 rounded-lg text-xs font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap"
              style={{ backgroundColor: '#00A67E' }}
            >
              <span>{t('nav.panels.builtFor.getIndustryDemo')}</span>
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Features mega panel (same style as Built For & Resources) ─── */
function FeaturesPanel({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <div className="absolute top-full left-0 right-0 pt-2 z-50" style={{ position: 'fixed', left: 0, right: 0, top: '60px' }}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-100/80 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div>
              <h3 className="text-base font-bold text-gray-900">{t('nav.panels.features.title')}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{t('nav.panels.features.subtitle')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/how-it-works"
                onClick={onClose}
                className="flex items-center space-x-1.5 px-3 py-2 rounded-lg text-xs font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              >
                <i className="ri-play-circle-line text-sm" />
                <span>{t('nav.howItWorks')}</span>
              </Link>
              <Link
                to="/features"
                onClick={onClose}
                className="flex items-center space-x-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-white hover:opacity-90 transition-opacity bg-blue-600 hover:bg-blue-700"
              >
                <span>{t('nav.panels.features.viewAll')}</span>
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
          </div>

          {/* Three columns */}
          <div className="grid grid-cols-12 gap-0">
            {/* Column 1: Tracking & Delivery */}
            <div className="col-span-5 p-6 border-r border-gray-100">
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#1769ff] flex items-center justify-center">
                  <i className="ri-map-pin-user-line text-white text-sm"></i>
                </div>
                <h4 className="text-sm font-bold text-gray-900">{t('nav.panels.features.trackingDelivery')}</h4>
              </div>
              <p className="text-[11px] text-gray-500 mb-4">{t('nav.panels.features.trackingDeliverySub')}</p>
              <div className="space-y-1">
                {featuresItemKeys.slice(0, 4).map((item, i) => (
                  <Link key={i} to={item.href} onClick={onClose} className="flex items-center space-x-3 p-2.5 rounded-xl hover:bg-gray-50 group">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white" style={{ backgroundColor: item.color }}>
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-1.5">
                        <span className="text-[13px] font-semibold text-gray-800 group-hover:text-gray-900 block">{t(`nav.items.features.${item.key}.label`)}</span>
                        {item.tag && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600">{item.tag}</span>
                        )}
                      </div>
                      <span className="text-[11px] text-gray-500 block">{t(`nav.items.features.${item.key}.description`)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Column 2: Operations & Insights */}
            <div className="col-span-4 p-6 border-r border-gray-100">
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#1769ff] flex items-center justify-center">
                  <i className="ri-dashboard-3-line text-white text-sm"></i>
                </div>
                <h4 className="text-sm font-bold text-gray-900">{t('nav.panels.features.operationsInsights')}</h4>
              </div>
              <p className="text-[11px] text-gray-500 mb-4">{t('nav.panels.features.operationsInsightsSub')}</p>
              <div className="space-y-1">
                {featuresItemKeys.slice(4, 8).map((item, i) => (
                  <Link key={i} to={item.href} onClick={onClose} className="flex items-center space-x-3 p-2.5 rounded-xl hover:bg-gray-50 group">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white" style={{ backgroundColor: item.color }}>
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-1.5">
                        <span className="text-[13px] font-semibold text-gray-800 group-hover:text-gray-900 block">{t(`nav.items.features.${item.key}.label`)}</span>
                        {item.tag && (
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${item.tag === 'New' ? 'bg-purple-50 text-purple-500' : 'bg-emerald-50 text-emerald-600'}`}>{item.tag}</span>
                        )}
                      </div>
                      <span className="text-[11px] text-gray-500 block">{t(`nav.items.features.${item.key}.description`)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Column 3: AI-First Platform highlight */}
            <div className="col-span-3 p-6">
              <div className="rounded-xl border border-gray-100 bg-gradient-to-br from-[#F5F8FF] to-[#EDF2FF] overflow-hidden shadow-sm h-full flex flex-col">
                <span className="inline-block mt-4 mx-4 px-2.5 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-wider" style={{ backgroundColor: '#1769ff' }}>
                  {t('nav.panels.features.aiFirst')}
                </span>
                <div className="flex-1 p-4 flex flex-col justify-center">
                  <div className="aspect-video rounded-lg overflow-hidden bg-gray-100 mb-3">
                    <img src="/images/ai-agents-feature.png" alt="" className="w-full h-full object-cover" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 leading-snug mb-1">{t('nav.panels.features.everyFeaturePowered')}</h4>
                  <p className="text-[11px] text-gray-500 leading-relaxed mb-4">{t('nav.panels.features.automatingDecisions')}</p>
                  <a
                    href={LITE_SIGNUP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onClose}
                    className="inline-flex items-center justify-center space-x-1.5 px-4 py-2.5 rounded-lg text-xs font-semibold text-white hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#1769ff' }}
                  >
                    <span>{t('nav.panels.features.startFreeTrial')}</span>
                    <i className="ri-arrow-right-line text-sm"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Footer CTA */}
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50/80 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-700">{t('nav.panels.features.readyToAutomate')}</p>
            <a
              href="https://meetings.hubspot.com/aditya145"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="flex items-center space-x-1.5 px-4 py-2.5 rounded-lg text-xs font-semibold text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#00A67E' }}
            >
              <span>{t('nav.panels.features.getDemo')}</span>
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Resources & Knowledge Hub mega panel ─── */
function ResourcesPanel({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <div className="absolute top-full left-0 right-0 pt-2 z-50" style={{ position: 'fixed', left: 0, right: 0, top: '60px' }}>
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-100/80 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div>
              <h3 className="text-base font-bold text-gray-900">{t('nav.panels.resources.title')}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{t('nav.panels.resources.subtitle')}</p>
            </div>
            <Link
              to="/resources"
              onClick={onClose}
              className="flex items-center space-x-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-white hover:opacity-90 transition-opacity bg-blue-600 hover:bg-blue-700"
            >
              <span>{t('nav.panels.resources.viewAll')}</span>
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>

          {/* Main content: Learning + Success Stories + Latest Report */}
          <div className="grid grid-cols-12 gap-0">
            {/* Column 1: Learning */}
            <div className="col-span-4 p-6 border-r border-gray-100">
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#1769ff] flex items-center justify-center">
                  <i className="ri-folder-open-line text-white text-sm"></i>
                </div>
                <h4 className="text-sm font-bold text-gray-900">{t('nav.panels.resources.learning')}</h4>
              </div>
              <div className="space-y-1 mt-4">
                {learningResourceKeys.map((item, i) =>
                  item.comingSoon ? (
                    <div key={i} className="flex items-center space-x-3 p-2.5 rounded-xl bg-gray-50/80 cursor-not-allowed">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#00A67E]/10 text-[#00A67E]">
                        <i className={`${item.icon} text-base`}></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[13px] font-semibold text-gray-600">{t(`nav.items.resources.${item.key}.label`)}</span>
                          <span className="text-[9px] font-bold uppercase tracking-wider text-[#00A67E] bg-[#00A67E]/10 px-1.5 py-0.5 rounded">{t('nav.comingSoon')}</span>
                        </div>
                        <span className="text-[11px] text-gray-500 block">{t(`nav.items.resources.${item.key}.description`)}</span>
                      </div>
                    </div>
                  ) : (
                    <Link key={i} to={item.href} onClick={onClose} className="flex items-center space-x-3 p-2.5 rounded-xl hover:bg-gray-50 group">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#00A67E]/10 text-[#00A67E]">
                        <i className={`${item.icon} text-base`}></i>
                      </div>
                      <div className="min-w-0">
                        <span className="text-[13px] font-semibold text-gray-800 group-hover:text-gray-900 block">{t(`nav.items.resources.${item.key}.label`)}</span>
                        <span className="text-[11px] text-gray-500 block">{t(`nav.items.resources.${item.key}.description`)}</span>
                      </div>
                    </Link>
                  )
                )}
              </div>
            </div>

            {/* Column 2: Success Stories */}
            <div className="col-span-4 p-6 border-r border-gray-100">
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#1769ff] flex items-center justify-center">
                  <i className="ri-team-line text-white text-sm"></i>
                </div>
                <h4 className="text-sm font-bold text-gray-900">{t('nav.panels.resources.successStories')}</h4>
              </div>
              <div className="space-y-1 mt-4">
                {successStoryKeys.map((item, i) => (
                  <Link key={i} to={item.href} onClick={onClose} className="flex items-center space-x-3 p-2.5 rounded-xl hover:bg-gray-50 group">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#F3EEFF] text-[#8B5CF6]">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div className="min-w-0">
                      <span className="text-[13px] font-semibold text-gray-800 group-hover:text-gray-900 block">{t(`nav.items.resources.${item.key}.label`)}</span>
                      <span className="text-[11px] text-gray-500 block">{t(`nav.items.resources.${item.key}.description`)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Column 3: Latest Report */}
            <div className="col-span-4 p-6">
              <div className="rounded-xl border border-gray-100 bg-gray-50/50 overflow-hidden shadow-sm h-full flex flex-col">
                <span className="inline-block mt-4 mx-4 px-2.5 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-wider" style={{ backgroundColor: '#00A67E' }}>
                  {t('nav.panels.resources.latestReport')}
                </span>
                <div className="flex-1 p-4 flex flex-col justify-center">
                  <div className="aspect-video rounded-lg overflow-hidden bg-gray-100 mb-3">
                    <img src={STEP_VISUALS[3].img} alt="" loading="lazy" className="w-full h-full object-cover object-top" />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 leading-snug mb-1">{t('nav.panels.resources.lastMile2026')}</h4>
                  <p className="text-[11px] text-gray-500 leading-relaxed mb-3">{t('nav.panels.resources.lastMileDesc')}</p>
                  <Link to="/how-it-works" onClick={onClose} className="text-xs font-semibold text-[#1769ff] hover:underline flex items-center space-x-1">
                    <span>{t('nav.panels.resources.readReport')}</span>
                    <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Support & Footer CTA */}
          <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-3">{t('nav.panels.resources.support')}</p>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center space-x-4">
                {supportKeys.map((item, i) => (
                  item.href.startsWith('/') ? (
                    <Link key={i} to={item.href} onClick={onClose} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-white group">
                      <div className="w-8 h-8 rounded-lg bg-[#FFF7ED] flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-[#EA580C] text-sm`}></i>
                      </div>
                      <span className="text-[13px] font-semibold text-gray-800 group-hover:text-gray-900">{t(`nav.items.resources.${item.key}.label`)}</span>
                    </Link>
                  ) : (
                    <a key={i} href={item.href} target="_blank" rel="noopener noreferrer" onClick={onClose} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-white group">
                      <div className="w-8 h-8 rounded-lg bg-[#FFF7ED] flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-[#EA580C] text-sm`}></i>
                      </div>
                      <span className="text-[13px] font-semibold text-gray-800 group-hover:text-gray-900">{t(`nav.items.resources.${item.key}.label`)}</span>
                    </a>
                  )
                ))}
              </div>
              <div className="flex items-center space-x-4">
                <p className="text-sm font-medium text-gray-700">{t('nav.panels.resources.needHelp')}</p>
                <a
                  href="https://meetings.hubspot.com/aditya145"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  className="flex items-center space-x-1.5 px-4 py-2.5 rounded-lg text-xs font-semibold text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#00A67E' }}
                >
                  <span>{t('nav.panels.resources.contactExperts')}</span>
                  <i className="ri-arrow-right-line text-sm"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Overlay backdrop (visual only — must not block page clicks; nav closes via click-outside) ─── */
function Overlay({ isOpen, dim }: { isOpen: boolean; dim?: boolean }) {
  if (!isOpen) return null;
  return (
    <div
      className={`pointer-events-none fixed inset-0 z-40 ${dim ? 'bg-black/50' : 'bg-black/5'}`}
      aria-hidden
    />
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenDropdown(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  const handleMouseEnter = (name: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenDropdown(name);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenDropdown(null), 200);
  };

  const closeDropdown = () => setOpenDropdown(null);

  const { t, i18n } = useTranslation();
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const currentLang = SUPPORTED_LANGS.find((l) => l.code === i18n.language) ?? SUPPORTED_LANGS[0];
  const builtForItemsForMobile = builtForItemKeys.map((i) => ({
    label: t(`nav.items.builtFor.${i.key}.label`),
    href: i.href,
    icon: i.icon,
  }));
  const featuresItemsForMobile = featuresItemKeys.map((i) => ({
    label: t(`nav.items.features.${i.key}.label`),
    href: i.href,
    icon: i.icon,
  }));
  const resourcesItemsForMobile = resourcesItemKeys.map((i) => ({
    label: t(`nav.items.resources.${i.key}.label`),
    href: i.href,
    icon: i.icon,
  }));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) setLangMenuOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const navButtonClass = (name: string) =>
    `flex items-center space-x-1 whitespace-nowrap px-2.5 xl:px-3.5 py-2 rounded-lg text-[13px] font-medium transition-all cursor-pointer ${
      openDropdown === name
        ? 'text-[#1769ff] bg-[#EEF4FF]'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
    }`;

  const linkClass =
    'whitespace-nowrap px-2.5 xl:px-3.5 py-2 rounded-lg text-[13px] font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all';

  const navBarBg = scrolled
    ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.05)] border-b border-gray-100/60'
    : 'bg-white border-b border-transparent';

  return (
    <>
      <Overlay isOpen={!!openDropdown} />
      <nav ref={navRef} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBarBg}`}>
        <div className="max-w-7xl mx-auto px-6 h-[60px] flex items-center justify-between">
          {/* Left: Logo + Nav */}
          <div className="flex items-center space-x-8">
            <Link to="/" onClick={closeDropdown}>
              <img
                src="https://cdn.geofleet.ai/images/logo.svg"
                alt="Geofleet.ai"
                className="h-7"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center space-x-0.5">
              {/* Built For */}
              <div onMouseEnter={() => handleMouseEnter('builtFor')} onMouseLeave={handleMouseLeave}>
                <button className={navButtonClass('builtFor')}>
                  <span>{t('nav.builtFor')}</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${openDropdown === 'builtFor' ? 'rotate-180' : ''}`}></i>
                </button>
              </div>

              {/* Features */}
              <div onMouseEnter={() => handleMouseEnter('features')} onMouseLeave={handleMouseLeave}>
                <button className={navButtonClass('features')}>
                  <span>{t('nav.features')}</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${openDropdown === 'features' ? 'rotate-180' : ''}`}></i>
                </button>
              </div>

              {/* Resources */}
              <div onMouseEnter={() => handleMouseEnter('resources')} onMouseLeave={handleMouseLeave}>
                <button className={navButtonClass('resources')}>
                  <span>{t('nav.resources')}</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${openDropdown === 'resources' ? 'rotate-180' : ''}`}></i>
                </button>
              </div>

              <Link to="/how-it-works" onClick={closeDropdown} className={linkClass}>{t('nav.howItWorks')}</Link>
              <Link to="/pricing" onClick={closeDropdown} className={linkClass}>{t('nav.pricing')}</Link>
              <Link to="/contact" onClick={closeDropdown} className={linkClass}>{t('nav.contact')}</Link>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center space-x-2">
            {/* Language switcher */}
            <div className="relative hidden sm:block" ref={langMenuRef}>
              <button
                type="button"
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors cursor-pointer text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                aria-expanded={langMenuOpen}
                aria-haspopup="true"
                aria-label="Select language"
              >
                <i className="ri-global-line text-base"></i>
                <span className="uppercase">{currentLang.code}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${langMenuOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langMenuOpen && (
                <div className="absolute top-full right-0 mt-1 py-1.5 bg-white rounded-xl border border-gray-100 shadow-lg min-w-[140px] z-50">
                  {SUPPORTED_LANGS.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => { i18n.changeLanguage(lang.code); setLangMenuOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-[13px] font-medium transition-colors flex items-center justify-between ${
                        i18n.language === lang.code ? 'text-[#1769ff] bg-[#eef5ff]' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span>{lang.name}</span>
                      {i18n.language === lang.code && <i className="ri-check-line text-[#1769ff]"></i>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <a
              href="https://app.geofleet.ai/login"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap text-[13px] font-medium transition-colors cursor-pointer hidden md:block px-3 py-2 text-gray-600 hover:text-gray-900"
            >
              {t('nav.login')}
            </a>
            <a
              href={LITE_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg text-[13px] font-semibold transition-all whitespace-nowrap cursor-pointer hidden sm:block hover:opacity-90"
              style={{
                backgroundColor: '#1769ff',
                color: 'white',
              }}
            >
              {t('nav.getStarted')}
            </a>

            {/* Mobile hamburger */}
            <button
              onClick={() => {
                setMobileOpen(!mobileOpen);
                setOpenDropdown(null);
              }}
              type="button"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              className="lg:hidden p-2 rounded-lg transition-colors cursor-pointer hover:bg-gray-100"
            >
              <i
                className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl text-gray-700`}
                aria-hidden
              ></i>
            </button>
          </div>
        </div>

        {/* Desktop dropdown panels with hover zones */}
        <div
          className="hidden lg:block"
          onMouseEnter={() => { if (openDropdown === 'builtFor') handleMouseEnter('builtFor'); }}
          onMouseLeave={handleMouseLeave}
        >
          <BuiltForPanel isOpen={openDropdown === 'builtFor'} onClose={closeDropdown} />
        </div>
        <div
          className="hidden lg:block"
          onMouseEnter={() => { if (openDropdown === 'features') handleMouseEnter('features'); }}
          onMouseLeave={handleMouseLeave}
        >
          <FeaturesPanel isOpen={openDropdown === 'features'} onClose={closeDropdown} />
        </div>
        <div
          className="hidden lg:block"
          onMouseEnter={() => { if (openDropdown === 'resources') handleMouseEnter('resources'); }}
          onMouseLeave={handleMouseLeave}
        >
          <ResourcesPanel isOpen={openDropdown === 'resources'} onClose={closeDropdown} />
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-1 max-h-[80vh] overflow-y-auto shadow-xl">
            {[
              { name: 'builtFor', label: t('nav.builtFor'), items: builtForItemsForMobile },
              { name: 'features', label: t('nav.features'), items: featuresItemsForMobile },
              { name: 'resources', label: t('nav.resources'), items: resourcesItemsForMobile },
            ].map((dd) => (
              <div key={dd.name}>
                <button
                  onClick={() => setOpenDropdown(openDropdown === dd.name ? null : dd.name)}
                  className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700"
                >
                  <span>{dd.label}</span>
                  <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${openDropdown === dd.name ? 'rotate-180' : ''}`}></i>
                </button>
                {openDropdown === dd.name && (
                  <div className="pl-2 pb-3 space-y-0.5">
                    {dd.items.map((item, i) =>
                      item.href.startsWith('/') ? (
                        <Link key={i} to={item.href} className="flex items-center space-x-2.5 py-2 px-2 rounded-lg text-sm text-gray-600 hover:bg-[#F5F8FF] hover:text-[#1769ff] transition-colors" onClick={() => setMobileOpen(false)}>
                          <i className={`${item.icon} text-[#1769ff] text-sm`}></i>
                          <span>{item.label}</span>
                        </Link>
                      ) : (
                        <a key={i} href={item.href} className="flex items-center space-x-2.5 py-2 px-2 rounded-lg text-sm text-gray-600 hover:bg-[#F5F8FF] hover:text-[#1769ff] transition-colors" onClick={() => setMobileOpen(false)}>
                          <i className={`${item.icon} text-[#1769ff] text-sm`}></i>
                          <span>{item.label}</span>
                        </a>
                      )
                    )}
                  </div>
                )}
              </div>
            ))}

            <Link to="/lite" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 py-3 text-sm font-medium text-gray-700 hover:text-[#1769ff]">
              {t('nav.lite')}
            </Link>
            <Link to="/how-it-works" onClick={() => setMobileOpen(false)} className="block py-3 text-sm font-medium text-gray-700 hover:text-[#1769ff]">{t('nav.howItWorks')}</Link>
            <Link to="/pricing" onClick={() => setMobileOpen(false)} className="block py-3 text-sm font-medium text-gray-700 hover:text-[#1769ff]">{t('nav.pricing')}</Link>
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="block py-3 text-sm font-medium text-gray-700 hover:text-[#1769ff]">{t('nav.contact')}</Link>

            {/* Mobile language switcher */}
            <div className="py-3 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('nav.mobileLanguage')}</p>
              <div className="flex flex-wrap gap-2">
                {SUPPORTED_LANGS.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => { i18n.changeLanguage(lang.code); setMobileOpen(false); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      i18n.language === lang.code ? 'bg-[#1769ff] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100 space-y-3">
            <a href="https://app.geofleet.ai/login" target="_blank" rel="noopener noreferrer" className="block py-2 text-sm font-medium text-gray-700">{t('nav.login')}</a>
            <a href={LITE_SIGNUP_URL} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="block text-center py-2.5 rounded-lg text-sm font-semibold hover:opacity-90" style={{ backgroundColor: '#1769ff', color: 'white' }}>{t('nav.getStarted')}</a>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
